import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as f3 from 'family-chart';
import 'family-chart/styles/family-chart.css';
import '../styles/family-chart-card.css';
import type { TreeGraph } from '@nasabtree/shared-types';
import type { TreeDatum, Chart } from 'family-chart';
import { treeGraphToDatum, findRootAncestor } from '../lib/treeGraphToDatum';

function getClassList(d: TreeDatum): string[] {
  const classList: string[] = [];
  if (d.data.data.gender === 'M') classList.push('card-male');
  else if (d.data.data.gender === 'F') classList.push('card-female');
  else classList.push('card-genderless');
  if (d.data.main) classList.push('card-main');
  return classList;
}

function getCardInnerImage(d: TreeDatum): string {
  return `
    <div class="card-image ${getClassList(d).join(' ')}">
      <img src="${d.data.data.avatar}" />
      <div class="card-label">${d.data.data.label}</div>
    </div>
  `;
}

function getCardInnerText(d: TreeDatum): string {
  return `
    <div class="card-text ${getClassList(d).join(' ')}">
      ${d.data.data.label}
    </div>
  `;
}

export function FamilyTreeView({
  graph,
  treeId,
  selectedPersonId: _selectedPersonId,
  onSelectPerson,
  onViewDetails,
}: {
  graph: TreeGraph;
  treeId: string;
  selectedPersonId: string | null;
  onSelectPerson: (id: string) => void;
  onViewDetails: (id: string) => void;
}) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const callbacksRef = useRef({ onSelectPerson, onViewDetails });

  useEffect(() => {
    callbacksRef.current = { onSelectPerson, onViewDetails };
  }, [onSelectPerson, onViewDetails]);

  useEffect(() => {
    if (!containerRef.current || graph.persons.length === 0) return;

    const container = containerRef.current;
    container.innerHTML = '';

    const data = treeGraphToDatum(graph, treeId);
    if (data.length === 0) return;

    const mainId = findRootAncestor(data) ?? data[0]!.id;

    const chart = f3.createChart(container, data)
      .setTransitionTime(1000)
      .setCardXSpacing(150)
      .setCardYSpacing(150);

    chart.setCardHtml()
      .setOnCardUpdate(function Card() {
        return function (this: HTMLElement, d: TreeDatum) {
          const card = this.querySelector('.card');
          if (card) {
            card.outerHTML = `
              <div class="card" style="transform: translate(-50%, -50%);">
                ${d.data.data.avatar ? getCardInnerImage(d) : getCardInnerText(d)}
              </div>
            `;
          }
          this.addEventListener('click', () => {
            callbacksRef.current.onSelectPerson(d.data.id);
            chart.updateMainId(d.data.id);
            chart.updateTree({});
          });
        };
      }());

    chart.updateMainId(mainId);
    chart.updateTree({ initial: true });

    chartRef.current = chart;

    // Double-click → open person detail
    function handleDblClick(e: MouseEvent) {
      const cardEl = (e.target as HTMLElement).closest('.card');
      if (cardEl) {
        const cardHtml = cardEl.closest('[data-id]');
        if (cardHtml) {
          const id = cardHtml.getAttribute('data-id');
          if (id) callbacksRef.current.onViewDetails(id);
        }
      }
    }
    container.addEventListener('dblclick', handleDblClick);

    return () => {
      container.removeEventListener('dblclick', handleDblClick);
      container.innerHTML = '';
      chartRef.current = null;
    };
  }, [graph, treeId]);

  if (graph.persons.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <p className="text-sm">{t('tree.noData')}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[calc(100vh-200px)] min-h-[400px] rounded-xl overflow-hidden">
      <div ref={containerRef} className="f3 w-full h-full nasab-chart" />
    </div>
  );
}
